import { useRegisterSupportOrganizationList } from "@/api/services/admin/rso";
import { Tab } from "@/components/elements/Tab";
import { TopPage } from "@/components/elements/TopPage";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { RsoListPagination } from "@/features/rso/components/RsoListPagination";
import { RsoListTop } from "@/features/rso/components/RsoListTop";
import { RsoTable, RSO_COMMON_SORT_FIELDS, RSO_TAB_DATE_SORT_FIELD } from "@/features/rso/components/RsoTable";
import { RSO_LIST_TAB, RSO_LIST_TAB_LABEL } from "@/features/rso/constants/rsoListTab";
import { useGetRsoTotalCount } from "@/features/rso/hooks/useRsoTotalCount";
import { useRsoFilterCache } from "@/features/rso/provider/RsoListFilterCacheProvider";
import { background, divider } from "@/theme/colors";
import { ERSOContractStatus } from "@/types/enum";
import { Box, Stack } from "@mui/material";
import { FC, useMemo } from "react";

type TRsoListPageProps = {};

export const RsoListPage: FC<TRsoListPageProps> = () => {
  const { rsoFilterCache, setRsoFilterCache } = useRsoFilterCache();

  const rsoCounts = useGetRsoTotalCount(rsoFilterCache?.keyword || undefined);

  const searchTab = rsoFilterCache?.tab || RSO_LIST_TAB.DURING_CONTRACT;

  const contractStatuses = useMemo(() => {
    if (searchTab === RSO_LIST_TAB.DURING_CONTRACT) return [ERSOContractStatus.DURING_CONTRACT];
    else if (searchTab === RSO_LIST_TAB.CANCELED_CONTRACT) return [ERSOContractStatus.CANCELED_CONTRACT];
    else if (searchTab === RSO_LIST_TAB.TRIAL) return [ERSOContractStatus.TRIAL];
    else if (searchTab === RSO_LIST_TAB.TRIAL_CANCELLED) return [ERSOContractStatus.TRIAL_CANCELLED];
    else return [ERSOContractStatus.DURING_CONTRACT];
  }, [searchTab]);

  // Sort is kept in cache across tabs, but only applied to the API when the sorted
  // column actually exists on the current tab. The cache stays untouched so returning
  // to the original tab restores its sort (unless the user picked a new sort meanwhile).
  const activeSorts = useMemo(() => {
    const validFields = [...RSO_COMMON_SORT_FIELDS, RSO_TAB_DATE_SORT_FIELD[searchTab]];
    return rsoFilterCache?.sorts?.filter((sort) => validFields.includes(sort.split("|")[0]));
  }, [rsoFilterCache?.sorts, searchTab]);

  const { data: rsoListResult, isLoading } = useRegisterSupportOrganizationList({
    page: rsoFilterCache?.page || START_PAGE,
    contractStatuses,
    perPage: DEFAULT_PER_PAGE,
    sorts: activeSorts,
    keyword: rsoFilterCache?.keyword || undefined,
  });

  return (
    <Stack gap={2.5}>
      <TopPage title="登録支援機関の一覧" enablePageBack={false} />
      <Stack
        sx={{
          border: `1px solid ${divider.middle}`,
          borderTop: "none",
          backgroundColor: background.white,
          borderRadius: "8px",
        }}
      >
        <Tab
          value={searchTab}
          onChange={(_, tab) => {
            setRsoFilterCache({ keyword: rsoFilterCache?.keyword, sorts: rsoFilterCache?.sorts, tab: tab.toString(), page: START_PAGE });
          }}
          options={[
            {
              label: `${RSO_LIST_TAB_LABEL[RSO_LIST_TAB.DURING_CONTRACT]} (${rsoCounts?.duringContractCount}) `,
              value: RSO_LIST_TAB.DURING_CONTRACT,
            },
            {
              label: `${RSO_LIST_TAB_LABEL[RSO_LIST_TAB.CANCELED_CONTRACT]} (${rsoCounts?.canceledContractCount}) `,
              value: RSO_LIST_TAB.CANCELED_CONTRACT,
            },
            {
              label: `${RSO_LIST_TAB_LABEL[RSO_LIST_TAB.TRIAL]} (${rsoCounts?.trialCount}) `,
              value: RSO_LIST_TAB.TRIAL,
            },
            {
              label: `${RSO_LIST_TAB_LABEL[RSO_LIST_TAB.TRIAL_CANCELLED]} (${rsoCounts?.trialCancelledCount}) `,
              value: RSO_LIST_TAB.TRIAL_CANCELLED,
            },
          ]}
        />
        <Box padding="24px">
          <Stack gap={3}>
            <RsoListTop pagination={rsoListResult?.pagination} />
            <RsoTable contractStatuses={contractStatuses} data={rsoListResult?.data ?? []} isLoading={isLoading} />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <RsoListPagination totalPage={rsoListResult?.pagination?.totalPage ?? 0} />
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
